import { useAppDispatch, useAppSelector } from '@redux/store';
import Pagination from '@mui/material/Pagination';
import { useCallback, useMemo } from 'react';

import { EmptyListSection, List } from '@3.0/index';
import useFetchDataSourceTables from '../../hooks/useFetchDataSourceTables.query';
import ListError from './components/ListError';
import { getNoTablesFoundText } from '../helpers';
import { setTablePageNumber } from './redux/IngestionDataSourceList.slice';
import InfoBanner from '@3.0/InfoBanner';
import DataSourceListItem from './DataSourceListItem';
import {
  DATASOURCE_BY_TABLE_TYPES,
  TDataSourceTypesAllowed,
} from './IngestionByDataSource.constants';
import cn from 'utils/cn';

const TableList = () => {
  const {
    data: tables,
    isLoading,
    isFetching,
    isError,
    isFetched,
    refetch,
  } = useFetchDataSourceTables();

  const selectedDataSource = useAppSelector(
    (state) => state.ingestionDataSourceList.selectedDataSource
  );
  const searchTerm = useAppSelector((state) => state.ingestionDataSourceList.tables.searchTerm);
  const pageLimit = useAppSelector((state) => state.ingestionDataSourceList.tables.pageLimit);
  const pageNumber = useAppSelector((state) => state.ingestionDataSourceList.tables.pageNumber);

  const emptyListLabel = useMemo(() => {
    return searchTerm.length > 0
      ? getNoTablesFoundText(selectedDataSource?.dataSourceType as TDataSourceTypesAllowed)
          .noneFound
      : getNoTablesFoundText(selectedDataSource?.dataSourceType as TDataSourceTypesAllowed).empty;
  }, [selectedDataSource, searchTerm]);

  const loadingLabel = useMemo(() => {
    return `Loading ${DATASOURCE_BY_TABLE_TYPES?.[selectedDataSource?.dataSourceType as TDataSourceTypesAllowed]?.toLocaleLowerCase()}s...`;
  }, [selectedDataSource]);

  const dispatch = useAppDispatch();

  const goToPage = useCallback(
    (event: React.ChangeEvent<unknown>, page: number) => {
      dispatch(setTablePageNumber({ pageNumber: page }));
    },
    [dispatch]
  );

  const isPaginationRequired = useMemo(
    () => tables.totalCount >= pageLimit,
    [tables.totalCount, pageLimit]
  );

  return (
    <section className="tw:h-[calc(100vh-160px)]">
      {isError && <ListError refetch={refetch} />}

      {isFetched && tables.data.length === 0 && (
        <EmptyListSection showRetry={isError} retryFn={refetch} label={emptyListLabel} />
      )}

      {(isLoading || isFetching) && (
        <>
          <InfoBanner label={loadingLabel} testId="loading-tables-banner" variant="loading" />
          <EmptyListSection
            label="Please wait..."
            className="tw:flex-1 tw:min-h-0 tw:overflow-y-auto"
          />
        </>
      )}

      <div className="tw:h-full tw:flex tw:flex-col tw:overflow-y-auto tw:bg-secondary-600/75">
        <List
          ulProps={{
            className: cn(
              'tw:bg-primary-100',
              isPaginationRequired && 'tw:h-[800px] tw:!overflow-y-auto'
            ),
          }}
        >
          <>
            {tables.data.map((table, index) => (
              <DataSourceListItem
                mode="table"
                key={index}
                listItem={{
                  name: table.tableName,
                  dataSourceType: selectedDataSource?.dataSourceType || 0,
                  id: selectedDataSource?.id || 0,
                }}
              />
            ))}
          </>
        </List>
        {isPaginationRequired && (
          <div className="tw:flex tw:items-center tw:justify-center tw:bg-primary-400 tw:border-y-primary-500 tw:border-y">
            <Pagination
              size="small"
              count={Math.ceil(tables.totalCount / pageLimit)}
              page={pageNumber}
              onChange={goToPage}
              className="tw:h-[40px] tw:flex tw:items-center"
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default TableList;
